import type {
  SupportAttachment,
  SupportMessage,
  SupportMessagesPage,
  SupportReaction,
  SupportRecentOrder,
  SupportTicket,
  SupportTicketType,
} from '../model/types'
import type { SupportStickerPack } from '../model/stickerPacks'
import { useApi } from '@shared/api'

/** Что отдаёт сервер по пакетам стикеров заведения */
interface ServerStickerPack {
  id: string
  title: string
  stickers: Array<{ id: string, url: string, mime: string, alt: string }>
}

/**
 * Серверный пакет → вид, в котором его рисует пикер. Lottie от картинки отличаем
 * по MIME с сервера, а не по расширению ссылки: ключ в S3 — uuid, и расширение
 * там наше, а не то, что прислал загружающий.
 */
function toStickerPack(pack: ServerStickerPack): SupportStickerPack {
  return {
    id: pack.id,
    title: pack.title,
    stickers: pack.stickers.map(sticker => ({
      id: sticker.id,
      alt: sticker.alt,
      src: sticker.url,
      kind: sticker.mime === 'application/json' ? 'lottie' : 'image',
    })),
  }
}

/** REST чата поддержки (клиентская сторона). Realtime-события приходят по socket.io. */
export const apiSupportChat = {
  async createTicket(payload: {
    type: SupportTicketType
    subject?: string
    orderId?: string
    text?: string
    attachments?: SupportAttachment[]
  }): Promise<{ ticket: SupportTicket, message: SupportMessage }> {
    const { data } = await useApi(true).post('/support/tickets', { body: payload })
    return data
  },

  async getMyTickets(): Promise<SupportTicket[]> {
    const { data } = await useApi(true).get('/support/tickets')
    return data.tickets
  },

  async getMessages(ticketId: string, cursor?: string): Promise<SupportMessagesPage> {
    const { data } = await useApi(true).get(`/support/tickets/${ticketId}/messages`, {
      params: cursor ? { cursor } : {},
    })
    return data
  },

  async sendMessage(
    ticketId: string,
    payload: {
      text?: string
      attachments?: SupportAttachment[]
      orderId?: string
      replyToId?: string
      stickerId?: string
    },
  ): Promise<SupportMessage> {
    const { data } = await useApi(true).post(`/support/tickets/${ticketId}/messages`, {
      body: payload,
    })
    return data
  },

  /**
   * Поставить/снять реакцию. emoji=null — снять; тот же эмодзи второй раз сервер
   * тоже трактует как снятие. Возвращает актуальный набор реакций сообщения.
   */
  async setReaction(
    ticketId: string,
    messageId: string,
    emoji: string | null,
  ): Promise<SupportReaction[]> {
    const { data } = await useApi(true).post(
      `/support/tickets/${ticketId}/messages/${messageId}/reaction`,
      { body: { emoji: emoji ?? '' } },
    )
    return data.reactions ?? []
  },

  async markRead(ticketId: string): Promise<SupportTicket> {
    const { data } = await useApi(true).post(`/support/tickets/${ticketId}/read`)
    return data.ticket
  },

  /** Подтвердить доставку сообщений админа (вторая галочка у него) */
  async markDelivered(ticketId: string): Promise<string[]> {
    const { data } = await useApi(true).post(`/support/tickets/${ticketId}/delivered`)
    return data.deliveredIds ?? []
  },

  async rateTicket(
    ticketId: string,
    payload: { rating: number, resolved?: boolean | null, fast?: boolean | null, comment?: string },
  ): Promise<SupportTicket> {
    const { data } = await useApi(true).post(`/support/tickets/${ticketId}/rating`, {
      body: {
        rating: payload.rating,
        ...(payload.resolved === null || payload.resolved === undefined ? {} : { resolved: payload.resolved }),
        ...(payload.fast === null || payload.fast === undefined ? {} : { fast: payload.fast }),
        ...(payload.comment ? { comment: payload.comment } : {}),
      },
    })
    return data.ticket
  },

  /**
   * Пакеты стикеров ЭТОГО заведения. Встроенный пакет сюда не входит — он лежит
   * в сборке фронта; сервер знает только про загруженные заведением.
   */
  async getStickerPacks(): Promise<SupportStickerPack[]> {
    const { data } = await useApi(true).get('/support/stickers')
    return (data.packs ?? []).map(toStickerPack)
  },

  async getRecentOrders(): Promise<SupportRecentOrder[]> {
    const { data } = await useApi(true).get('/support/orders/recent')
    return data.orders
  },

  /** Загрузка вложения (фото или видео) — файл уходит в S3 сразу, до отправки сообщения */
  async uploadMedia(file: File): Promise<SupportAttachment> {
    const body = new FormData()
    body.append('file', file)
    const { data } = await useApi(true).post('/support/upload', { body })
    return data
  },
}
